import type { ReactNode } from 'react'
import { Button, Tag } from 'antd'
import type { ColumnType } from 'antd/es/table'
import { formatShanghaiDate, formatShanghaiDateTime } from '@/const/date.ts'
import { formatAmount } from '@/utils/money.ts'
import { BILL_COLLECTION_STATUS, BILL_KIND_COLORS, BILL_KINDS } from './const/bill-search.ts'
import type { GroupDetailData } from './finance.types.ts'
import { snapshotObject } from './utils/finance-snapshot.ts'

function collectionStatusColor(status: keyof typeof BILL_COLLECTION_STATUS) {
  if (BILL_COLLECTION_STATUS[status].status === 'Success') {
    return 'green'
  }
  if (status === 'partial') {
    return 'blue'
  }
  if (status === 'refundDue') {
    return 'orange'
  }
  return 'default'
}

export type GroupBillTableRow = {
  billId: number
  billSnapshot: unknown
  outstandingAmount: string
  outstandingAmountSnapshot?: string
  feeType?: GroupDetailData['items'][number]['feeType']
  collectionStatus?: GroupDetailData['items'][number]['collectionStatus']
  latestSettlementAt?: Date | null
}

function billPeriodDate(value: unknown) {
  if (typeof value !== 'string' && !(value instanceof Date)) return '—'
  return formatShanghaiDate(value)
}

type GroupBillColumn<TRow> = Pick<ColumnType<TRow>, 'key' | 'dataIndex' | 'width' | 'align'> & {
  title: string
  render?: (value: unknown, row: TRow, index: number) => ReactNode
  search?: false
}

export function createGroupBillColumns<TRow extends GroupBillTableRow>({
  onBill,
  amountField = 'outstandingAmountSnapshot',
  amountTitle = '本次应收'
}: {
  onBill?: (id: number) => void
  amountField?: 'outstandingAmountSnapshot' | 'outstandingAmount'
  amountTitle?: string
} = {}): GroupBillColumn<TRow>[] {
  const columns: GroupBillColumn<TRow>[] = [
    {
      title: '账单号',
      width: '12rem',
      key: 'billNo',
      render: (_, row) => {
        const billNo = String(snapshotObject(row.billSnapshot).billNo ?? '—')
        if (!onBill) return billNo
        return (
          <Button type="link" className="h-auto! p-0! print:text-black" onClick={() => onBill(row.billId)}>
            {billNo}
          </Button>
        )
      }
    },
    {
      title: '订单',
      key: 'sourceOrderNo',
      width: '12rem',
      render: (_, row) => String(snapshotObject(row.billSnapshot).sourceOrderNo ?? '—')
    },
    {
      title: '业务类型',
      key: 'businessName',
      render: (_, row) => String(snapshotObject(row.billSnapshot).businessName ?? '—')
    },
    {
      title: '费用类型',
      dataIndex: 'feeType',
      render: (_, row) =>
        row.feeType ? <Tag color={BILL_KIND_COLORS[row.feeType]}>{BILL_KINDS[row.feeType]}</Tag> : '—'
    },
    {
      title: '费用期间',
      key: 'period',
      width: '12rem',
      render: (_, r) => (
        <span className="text-xs leading-5 whitespace-nowrap tabular-nums">
          {billPeriodDate(snapshotObject(r.billSnapshot).periodStart)} 至{' '}
          {billPeriodDate(snapshotObject(r.billSnapshot).periodEnd)}
        </span>
      )
    },
    {
      title: amountTitle,
      key: 'amount',
      align: 'right',
      render: (_, row) => (
        <span className="font-medium tabular-nums">¥{formatAmount(row[amountField] ?? row.outstandingAmount)}</span>
      )
    },
    {
      title: '结算状态',
      dataIndex: 'collectionStatus',
      render: (_, row) =>
        row.collectionStatus ? (
          <Tag color={collectionStatusColor(row.collectionStatus)}>
            {BILL_COLLECTION_STATUS[row.collectionStatus].text}
          </Tag>
        ) : (
          '—'
        )
    },
    {
      title: '最近结算时间',
      dataIndex: 'latestSettlementAt',
      render: (_, row) => (row.latestSettlementAt ? formatShanghaiDateTime(row.latestSettlementAt) : '—')
    }
  ]
  return columns.map((column) => ({ ...column, search: false }))
}
